'use client';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton
} from '@mui/material';
import SnackBar from 'src/components/era-package/era/SnackBar';
import RenderFormSection from 'src/components/era-package/forms/RenderFormSection';
import Button from 'src/components/era-package/forms/Button';
import Buttons from 'src/components/era-package/era/Buttons';
import { useUnsavedAlert } from 'src/utils/era-package/setupUnsavedAlert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { addTabKeyToOptions } from 'src/utils/era-package/tab-utils';

const EditModal = ({
  title = 'Record',
  open,
  onClose,
  onSave,
  onDelete,
  fields, name,
  optionsMap,
  initialData,
  isEditing, index,
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const rhfArray = useFieldArray({ name }) || null
  const rhf = useFormContext() || null
  const [resetData, setResetData] = useState(null) // need to cache this data; formData & initialData will change

  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);

  const fieldIndex = isEditing ? index : rhfArray?.fields?.length
  const indexedName = `${name}.${fieldIndex}`

  // Refs for focus management
  const dialogRef = useRef(null);

  // Set up unsaved alert functionality
  useUnsavedAlert(hasUnsaved);

  const modalFields = useMemo(() => {
    return !!rhfArray
      ? fields.map(field => {
        let copy = { ...field }
        copy.name = `${indexedName}.${copy.name}`
        return copy
      })
      : fields
  }, [fields, indexedName, rhfArray])

  const indexedOptions = useMemo(() => {
    return !!rhfArray
      ? addTabKeyToOptions(optionsMap, indexedName)
      : optionsMap
  }, [optionsMap, indexedName, rhfArray])

  // Comprehensive focus management to prevent aria-hidden conflicts
  useEffect(() => {
    if (open) {

      // Immediately blur all focused elements in main wrapper when modal opens
      const mainWrapper = document.querySelector('.mainwrapper');
      if (mainWrapper) {
        const allFocusedElements = mainWrapper.querySelectorAll(':focus');
        allFocusedElements.forEach(element => {
          element.blur();
        });

        // Also blur any buttons that might retain focus
        const allButtons = mainWrapper.querySelectorAll('button, [role="button"]');
        allButtons.forEach(button => {
          if (document.activeElement === button) {
            button.blur();
          }
        });
      }

      // Set up continuous monitoring to catch any focus that slips through
      const focusMonitor = setInterval(() => {
        const mainWrapper = document.querySelector('.mainwrapper');
        const focusedElement = document.activeElement;

        if (mainWrapper &&
          mainWrapper.getAttribute('aria-hidden') === 'true' &&
          focusedElement &&
          mainWrapper.contains(focusedElement)) {

          // Immediately blur the problematic element
          focusedElement.blur();

          // Force focus to modal
          const dialog = dialogRef.current;
          if (dialog) {
            const firstFocusable = dialog.querySelector(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (firstFocusable) {
              firstFocusable.focus();
            }
          }
        }
      }, 50); // More frequent monitoring

      return () => {
        clearInterval(focusMonitor);
      };
    }
  }, [open]);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      setResetData({ ...initialData })
    } else {
      // Initialize with default values
      const defaults = {};
      fields.forEach(field => {
        defaults[field.name] = field.defaultValue;
      });
      setFormData(defaults);
    }
    // Reset unsaved state when modal opens/closes or initial data changes
    setHasUnsaved(false);
  }, [initialData, fields, open]);

  const validateForm = async () => {
    const newErrors = {};

    if (!!rhf) {
      if (rhf.getValues(indexedName) !== undefined) {
        const err = await rhf.trigger(indexedName)
        return err
      } else {
        // item is empty.
        setShowErrorSnackbar(true)
        return false
      }
    }

    // no RHF. Manual validation
    modalFields.forEach(field => {
      if (field.required) {
        const value = formData[field.name];
        if (!value || String(value).trim() === '') {
          newErrors[field.name] = `${field.label} is required`;
        }
      }
    });

    setErrors(newErrors);
    console.log('errors', newErrors)
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    let isValid = await validateForm()
    if (isValid) {
      setHasUnsaved(false); // Clear unsaved flag on successful save
      const toSend = !!rhf ? rhf.getValues(indexedName) : formData
      onSave(toSend, rhfArray);
    }
  };

  const handleClose = () => {
    if (hasUnsaved) {
      const message = 'You have unsaved changes. Are you sure you want to close without saving?';
      if (window.confirm(message)) {
        setHasUnsaved(false);
        if (!!rhfArray) {
          if (isEditing) // reset data for this index
            rhfArray.update(fieldIndex, resetData)
          else
            rhfArray.remove(fieldIndex)
        }
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Handle keyboard navigation for accessibility
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleClose();
    } else if (event.key === 'Tab') {
      // Ensure focus stays within the modal
      const dialog = dialogRef.current;
      if (dialog) {
        const focusableElements = dialog.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    }
  };

  const formSection = {
    title: 'Details',
    fields: modalFields,
  };

  return (<>
    {/* Error Snackbar */}
    <SnackBar
      title="Missing Data"
      type="error"
      open={showErrorSnackbar}
      onClose={() => setShowErrorSnackbar(false)}
      duration={10000}
    >
      Nothing to add.
    </SnackBar>

    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="100dvw"
      fullWidth
      ref={dialogRef}
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
      TransitionProps={{
        onEntered: () => {
          // Comprehensive cleanup of any remaining focused elements in main wrapper
          const mainWrapper = document.querySelector('.mainwrapper');
          if (mainWrapper) {
            // Find and blur any focused elements
            const focusedElements = mainWrapper.querySelectorAll(':focus');
            focusedElements.forEach(element => {
              element.blur();
            });

            // Extra safety: blur any MUI buttons that might be retaining focus
            const muiButtons = mainWrapper.querySelectorAll('.MuiButton-root, .MuiButtonBase-root');
            muiButtons.forEach(button => {
              if (document.activeElement === button) {
                button.blur();
              }
            });
          }

          // Ensure focus is properly set in the modal
          setTimeout(() => {
            const dialog = dialogRef.current;
            if (dialog) {
              const firstFocusable = dialog.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
              );
              if (firstFocusable) {
                firstFocusable.focus();
              }
            }
          }, 10);
        }
      }}
      // Ensure proper focus containment
      disableEnforceFocus={false}
      disableAutoFocus={false}
      disableRestoreFocus={false}
      onKeyDown={handleKeyDown}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      role="dialog"
    >
      <DialogTitle
        id="modal-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        {`${isEditing ? 'Edit' : 'Add'} ${title}`}
        <IconButton
          aria-label={`Close ${isEditing ? 'edit' : 'add'} ${title} modal`}
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
          title="Close modal"
        >
          <Box component={FontAwesomeIcon} icon={faClose} sx={{ width: '1em' }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers id="modal-description">
        <Box sx={{ py: 2 }}>
          <RenderFormSection
            section={formSection}
            optionsMap={indexedOptions}
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            arrayKey={indexedName}
            setErrors={setErrors}
            setHasUnsaved={setHasUnsaved}
            globalAccordionAction={null} // Not needed for modal
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ pt: 3 }}>
        <Buttons
          advance={
            <Button
              onClick={handleSave}
              variant="contained"
              title={isEditing ? 'Save changes' : 'Add this record'}
            >
              {isEditing ? 'Save' : 'Add'}
            </Button>
          }
          other={
            <>
              <Button variant="outlined" onClick={handleClose} title="Go back">
                Cancel
              </Button>
              {isEditing && (
                <Button
                  variant="outlined"
                  onClick={onDelete}
                  color="error"
                  title="Proceed with Caution: Delete record"
                >
                  Delete
                </Button>
              )}
            </>
          }
        />
      </DialogActions>
    </Dialog>
  </>);
};

export default EditModal;
